import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import API_BASE_URL from '../config';
import './AllModal.css';
import ImageUploadModal from './ImageUploadModal';


const AddProposalHotelModal = ({ proposalId, proposal, onSave, onClose, editingHotel }) => {
  const [hotelName, setHotelName] = useState('');
  const [googleMapsLink, setGoogleMapsLink] = useState('');
  const [hotelUrl, setHotelUrl] = useState('');
  const [locationDescription, setLocationDescription] = useState('');
  const [cancellationPolicy, setCancellationPolicy] = useState('');
  const [selectedDates, setSelectedDates] = useState([]);
  const [dateRates, setDateRates] = useState({});
  const [featuresHtml, setFeaturesHtml] = useState('');

  const [error, setError] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [freeDates, setFreeDates] = useState({}); // Add free dates state
  const [score, setScore] = useState('');
  const [selectedDestination, setSelectedDestination] = useState('');
  const [rateTitle, setRateTitle] = useState('');
  const [taxes, setTaxes] = useState([]);
  const [fees, setFees] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [taxIncluded, setTaxIncluded] = useState(false);
  const [images, setImages] = useState([]);
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [paymentDue, setPaymentDue] = useState('100% at booking');
  const [customPaymentAmount, setCustomPaymentAmount] = useState('');
  const [notesHtml, setNotesHtml] = useState('');

  // React-Quill configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['blockquote'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline',
    'list', 'bullet', 'indent', 'blockquote'
  ];
  const modalRef = useRef(null);

  // Generate calendar dates based on proposal date range
  const generateCalendarDates = () => {
    const dates = [];
    
    if (proposal && proposal.startDate && proposal.endDate) {
      const start = new Date(proposal.startDate);
      const end = new Date(proposal.endDate);
      
      console.log('📅 Proposal range:', start.toDateString(), 'to', end.toDateString());
      
      // Generate dates from start to end (inclusive)
      let current = new Date(start);
      while (current <= end) {
        dates.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
      
      console.log('✅ Generated', dates.length, 'dates for calendar');
    } else {
      console.log('⚠️ No proposal dates available');
    }
    
    return dates;
  };

  // Move calendar dates calculation inside component
  const calendarDates = React.useMemo(() => {
    const dates = [];
    
    if (!proposal || !proposal.startDate || !proposal.endDate) {
      console.log('⚠️ No proposal dates available for calendar');
      return [];
    }
    
    try {
      const start = new Date(proposal.startDate);
      const end = new Date(proposal.endDate);
      
      console.log('📅 Modal calendar range:', start.toDateString(), 'to', end.toDateString());
      
      // Validate date range
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        console.error('❌ Invalid date format in modal');
        return [];
      }
      
      if (start > end) {
        console.error('❌ Start date is after end date in modal');
        return [];
      }
      
      // Generate dates from start to end (inclusive)
      let current = new Date(start);
      while (current <= end) {
        dates.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
      
      console.log('✅ Modal generated', dates.length, 'dates for calendar');
      return dates;
      
    } catch (error) {
      console.error('❌ Error generating calendar dates in modal:', error);
      return [];
    }
  }, [proposal]);

  // Close modal on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.className === 'modal-overlay') {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Prevent scroll from affecting number inputs
  useEffect(() => {
    const handleWheel = (event) => {
      // Check if the target is a number input that's focused
      if (event.target.type === 'number' && event.target === document.activeElement) {
        event.preventDefault();
      }
    };

    // Add event listener with passive: false to allow preventDefault
    document.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      document.removeEventListener('wheel', handleWheel);
    };
  }, []);

  // Handle ESC key press
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [onClose]);

  // Populate form when editing existing hotel
  useEffect(() => {
    if (editingHotel) {
      setHotelName(editingHotel.name || '');
      setGoogleMapsLink(editingHotel.googleMapsLink || '');
      setHotelUrl(editingHotel.hotelUrl || '');
      setLocationDescription(editingHotel.locationDescription || '');
      setCancellationPolicy(editingHotel.cancellationPolicy || '');
      setSelectedDestination(editingHotel.destination || '');
      // Convert from new dateRates array format to old format for UI
      if (editingHotel.dateRates && Array.isArray(editingHotel.dateRates)) {
        const dates = editingHotel.dateRates.map(item => item.date);
        const rates = {};
        const freeStates = {};
        editingHotel.dateRates.forEach(item => {
          rates[item.date] = item.rate.toString();
          // If rate is 0, mark as free
          freeStates[item.date] = parseFloat(item.rate) === 0;
        });
        setSelectedDates(dates);
        setDateRates(rates);
        setFreeDates(freeStates);
      } else {
        // Fallback for old format
        setSelectedDates(editingHotel.selectedDates || []);
        const rates = editingHotel.dateRates || {};
        setDateRates(rates);
        // Initialize freeDates based on which rates are 0
        const freeStates = {};
        Object.keys(rates).forEach(date => {
          freeStates[date] = parseFloat(rates[date]) === 0;
        });
        setFreeDates(freeStates);
      }
      console.log('Editing hotel data:', editingHotel);
      console.log('Editing hotel features:', editingHotel.features);
      console.log('Editing hotel features type:', typeof editingHotel.features);
      console.log('Editing hotel features isArray:', Array.isArray(editingHotel.features));


      setCurrency(editingHotel.currency || 'USD');
      setScore(editingHotel.score || '');
      setRateTitle(editingHotel.rateTitle || '');
      setTaxes(editingHotel.taxes || []);
      setFees(editingHotel.fees || []);
      setDiscounts(editingHotel.discounts || []);
      setTaxIncluded(editingHotel.taxIncluded || false);
      setImages(editingHotel.images || []);
      setPaymentDue(editingHotel.paymentDue || '100% at booking');
      setCustomPaymentAmount(editingHotel.customPaymentAmount || '');
      setNotesHtml(editingHotel.notes || '');
    } else {
      // Reset freeDates when not editing (creating new hotel)
      setFreeDates({});
    }
  }, [editingHotel]);

  // Initialize features HTML when editing
  useEffect(() => {
    console.log('🔄 Initializing features for editing:', editingHotel?.features);
    console.log('🔄 Features type:', typeof editingHotel?.features);
    console.log('🔄 Features isArray:', Array.isArray(editingHotel?.features));
    
    if (editingHotel?.features) {
      if (typeof editingHotel.features === 'string') {
        // New format: features is already HTML string
        console.log('✅ Using HTML string format:', editingHotel.features);
        setFeaturesHtml(editingHotel.features);
      } else if (Array.isArray(editingHotel.features) && editingHotel.features.length > 0) {
        // Old format: convert features array to HTML
        console.log('🔄 Converting array format to HTML:', editingHotel.features);
        const featuresHtml = editingHotel.features
          .map(feature => {
            if (feature.isBold) {
              return `<strong>${feature.text}</strong>`;
            }
            return feature.text;
          })
          .join('<br>');
        console.log('✅ Converted HTML:', featuresHtml);
        setFeaturesHtml(featuresHtml);
      } else {
        // Empty or invalid features
        console.log('⚠️ Empty or invalid features, setting empty string');
        setFeaturesHtml('');
      }
    } else {
      // No features
      console.log('⚠️ No features found, setting empty string');
      setFeaturesHtml('');
    }
  }, [editingHotel]);


  // Auto-select destination when there's only one option
  useEffect(() => {
    if (proposal?.destinations && proposal.destinations.length === 1 && !selectedDestination) {
      setSelectedDestination(proposal.destinations[0]);
    }
  }, [proposal?.destinations, selectedDestination]);

  // Scroll to top when modal opens for editing
  useEffect(() => {
    if (editingHotel && modalRef.current) {
      // Small delay to ensure DOM is fully rendered
      const timer = setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.scrollTop = 0;
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [editingHotel]);

  // Ensure modal starts at top when first opened
  useEffect(() => {
    const timer = setTimeout(() => {
      if (modalRef.current) {
        modalRef.current.scrollTop = 0;
      }
    }, 50);
    return () => clearTimeout(timer);
  }, []);



  const handleDateClick = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    setSelectedDates(prev => {
      if (prev.includes(dateStr)) {
        // Remove date and its rate
        const newDates = prev.filter(d => d !== dateStr);
        const newRates = { ...dateRates };
        delete newRates[dateStr];
        setDateRates(newRates);
        return newDates;
      } else {
        return [...prev, dateStr].sort();
      }
    });
  };

  const handleDateRateChange = (dateStr, rate) => {
    // Use proper rounding to avoid floating point precision issues
    const parsedRate = parseFloat(rate) || 0;
    const roundedRate = Math.round(parsedRate * 100) / 100; // Round to 2 decimal places

    setDateRates(prev => ({
      ...prev,
      [dateStr]: roundedRate
    }));
  };









  const handleSubmit = async (createNew = true) => {
    try {
      // Validate that at least one date is selected
      if (!selectedDates || selectedDates.length === 0) {
        alert('Please select at least one stay date before saving the hotel.');
      return;
    }

      let name = hotelName.trim() || 'Hotel Name';

      // Validate required fields
      if (!name || name === 'Hotel Name') {
        alert('Please enter a hotel name.');
        return;
      }

      const requestData = {
        name: name,
        destination: selectedDestination || (proposal?.destinations?.[0] || ''),
        googleMapsLink: googleMapsLink.trim(),
        hotelUrl: hotelUrl.trim(),
        locationDescription: locationDescription.trim(),
        cancellationPolicy: cancellationPolicy.trim(),
        selectedDates: selectedDates,
        dateRates: dateRates,
        currency: currency,
        rateTitle: rateTitle.trim(),
        taxes: taxes.filter(tax =>
          tax.title.trim() && tax.amount
        ).map(tax => ({
          title: tax.title.trim(),
          type: 'percentage',
          amount: Math.round(parseFloat(tax.amount) * 100) / 100
        })),
        fees: fees.filter(fee =>
          fee.title.trim() && fee.amount
        ).map(fee => ({
          title: fee.title.trim(),
          type: 'fixed',
          amount: Math.round(parseFloat(fee.amount) * 100) / 100
        })),
        discounts: discounts.filter(discount =>
          discount.title.trim() && discount.amount
        ).map(discount => ({
          title: discount.title.trim(),
          type: discount.type,
          amount: Math.round(parseFloat(discount.amount) * 100) / 100
        })),
        features: featuresHtml || '',
        status: 'pending',
        currency: currency,
        score: score.trim(),
        taxIncluded: taxIncluded,
        images: images,
        paymentDue: paymentDue,
        customPaymentAmount: customPaymentAmount,
        notes: notesHtml
      };

      // Ensure proper content-type header
      const config = {
        headers: {
          'Content-Type': 'application/json'
        }
      };

      let response;
      if (editingHotel && !createNew) {
        // Update existing hotel
        console.log('🔄 CLIENT: Sending UPDATE request to server');
        console.log('🔄 CLIENT: Update data keys:', Object.keys(requestData));
        console.log('🔄 CLIENT: Hotel ID:', editingHotel._id);
        console.log('🔄 CLIENT: Full request data:', JSON.stringify(requestData, null, 2));

        response = await axios.put(`${API_BASE_URL}/api/proposals/${proposalId}/hotels/${editingHotel._id}`, requestData, config);

        console.log('✅ CLIENT: Server response received');
        console.log('✅ CLIENT: Response status:', response.status);
        console.log('✅ CLIENT: Response data:', response.data);
      } else {
        // Create new hotel
        console.log('➕ Creating new hotel with data:', requestData);
        console.log('🔍 Frontend sending features:', requestData.features);
        console.log('🔍 Frontend features type:', typeof requestData.features);
        console.log('🔍 Frontend features isArray:', Array.isArray(requestData.features));
        response = await axios.post(`${API_BASE_URL}/api/proposals/${proposalId}/hotels`, requestData, config);
        console.log('✅ Hotel saved successfully:', response.data);
      }

      console.log('📤 Sending to onSave:', response.data.data);
      
      // If we have images and this is a new hotel, upload them now
      if (images.length > 0 && !editingHotel) {
        try {
          const formData = new FormData();
          
          // Add images to form data
          images.forEach((image) => {
            if (image.file) {
              formData.append('images', image.file);
            }
          });

          // Upload images to the newly created hotel
          await axios.post(
            `${API_BASE_URL}/api/proposals/${proposalId}/hotels/${response.data.data._id}/images`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }
          );
          
          console.log('✅ Images uploaded successfully');
        } catch (imageError) {
          console.error('❌ Error uploading images:', imageError);
          // Don't fail the entire operation if image upload fails
        }
      }
      
      onSave(response.data.data);
      onClose();
    } catch (err) {
      console.error(editingHotel && !createNew ? 'Error updating hotel:' : 'Error adding hotel:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      setError(`Failed to ${editingHotel && !createNew ? 'update' : 'add'} hotel: ` + (err.response?.data?.error || err.message));
    }
  };


  const handleFreeToggle = (dateStr) => {
    setFreeDates(prev => ({
      ...prev,
      [dateStr]: !prev[dateStr]
    }));

    // If marking as free, clear the rate
    if (!freeDates[dateStr]) {
      setDateRates(prev => ({
        ...prev,
        [dateStr]: 0
      }));
    }
  };

  const handleImagesUploaded = (uploadedImages) => {
    // Store both the preview URLs and file objects for later upload
    const imagesWithFiles = uploadedImages.map(image => ({
      ...image,
      file: image.file || null // Keep the file object if it exists
    }));
    setImages(imagesWithFiles);
    setShowImageUploadModal(false);
  };

  const removeImage = (imageId) => {
    setImages(prev => prev.filter(img => img.id !== imageId));
  };



  return (
    <div className="add-proposal-hotel-modal">
      <div className="modal-overlay">
        <div className="modal" ref={modalRef}>
          <h3>{editingHotel ? 'Edit Hotel' : 'Add Hotel to Proposal'}</h3>
          
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-container">
            {/* Row 1: Hotel Name - Destination */}
            {/* Hotel Name */}
            <div className="form-group">
              <label>Hotel Name *</label>
              <input
                type="text"
                value={hotelName}
                onChange={(e) => setHotelName(e.target.value)}
                placeholder="Enter hotel name"
                className="form-input"
              />
            </div>
            
            {/* Destination Selection */}
            <div className="form-group">
              <label>Destination *</label>
              <select
                value={selectedDestination}
                onChange={(e) => setSelectedDestination(e.target.value)}
                className="form-input"
                required
              >
                {proposal?.destinations && proposal.destinations.length > 1 && (
                  <option value="">Select a destination...</option>
                )}
                {proposal?.destinations && proposal.destinations.length > 0 ? (
                  proposal.destinations.map((destination, index) => (
                    <option key={index} value={destination}>
                      {destination}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>No destinations available</option>
                )}
              </select>
              </div>

            {/* Row 2: Google Maps Link - Rate Title */}
            {/* Google Maps Link */}
            <div className="form-group">
              <label>Google Maps Link</label>
              <input
                type="url"
                value={googleMapsLink}
                onChange={(e) => setGoogleMapsLink(e.target.value)}
                placeholder="https://maps.google.com/..."
                className="form-input"
              />
            </div>

            {/* Rate Title */}
            <div className="form-group">
              <label>Rate Title</label>
              <input
                type="text"
                value={rateTitle}
                onChange={(e) => setRateTitle(e.target.value)}
                placeholder="e.g., Standard Rate, Premium Room, Deluxe Suite"
                className="form-input"
              />
            </div>

            {/* Row 3: Hotel Website URL - Currency */}
            {/* Hotel URL */}
            <div className="form-group">
              <label>Hotel Website URL</label>
              <input
                type="url"
                value={hotelUrl}
                onChange={(e) => setHotelUrl(e.target.value)}
                placeholder="https://www.hotelwebsite.com"
                className="form-input"
              />
            </div>

            {/* Currency Selector */}
            <div className="form-group">
              <label>Currency for Rates</label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="form-input"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '3px',
                  backgroundColor: '#fff',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                <option value="USD">$ USD</option>
                <option value="EUR">€ EUR</option>
                <option value="MXN">$ MXN</option>
              </select>
            </div>

            {/* Row 4: Location Description - Taxes */}
            {/* Location Description */}
            <div className="form-group">
              <label>Location Description</label>
              <textarea
                value={locationDescription}
                onChange={(e) => setLocationDescription(e.target.value)}
                placeholder="Describe the hotel's location, nearby attractions, transportation options..."
                rows="3"
                className="form-textarea"
                style={{ resize: 'none' }} // Make non-resizable
              />
            </div>

            {/* Taxes */}
            <div className="form-group">
              <label>
                Taxes 
                <a 
                  href="https://www.calculatorsoup.com/calculators/financial/sales-tax-calculator.php" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    marginLeft: '8px', 
                    color: '#007bff', 
                    textDecoration: 'none',
                    fontSize: '0.9em'
                  }}
                >
                  TaxCalc
                </a>
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                <input
                  className="form-input"
                  type="text"
                  placeholder="Enter tax name (e.g., VAT, Sales Tax)"
                  style={{ flex: 1 }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const nameInput = e.target;
                      const container = nameInput.closest('.form-group');
                      const amountInput = container.querySelector('input[type="number"]');
                      
                      const name = nameInput.value.trim();
                      const amount = parseFloat(amountInput.value);
                      
                      if (name && amount > 0) {
                        const newTax = { title: name, amount: amount };
                        setTaxes(prev => [...prev.filter(t => t.title && t.title.trim() !== ''), newTax]);
                        nameInput.value = '';
                        amountInput.value = '';
                      }
                    }
                  }}
                />
                <input
                  className="form-input"
                  type="number"
                  placeholder="%"
                  step="0.01"
                  min="0"
                  max="100"
                  style={{ flex: '0 0 100px' }}
                />
                <button
                  type="button"
                  onClick={(e) => {
                    const container = e.target.closest('.form-group');
                    const nameInput = container.querySelector('input[type="text"]');
                    const amountInput = container.querySelector('input[type="number"]');
                    
                    const name = nameInput?.value.trim();
                    const amount = parseFloat(amountInput?.value);
                    
                    if (name && amount > 0) {
                      const newTax = { title: name, amount: amount };
                      setTaxes(prev => [...prev.filter(t => t.title && t.title.trim() !== ''), newTax]);
                      nameInput.value = '';
                      amountInput.value = '';
                    }
                  }}
                  className="add-button"
                  title="Add tax"
                >
                  +
                </button>
              </div>

              {/* Display added taxes as tags */}
              {taxes.filter(tax => tax.title && tax.title.trim() !== '').length > 0 ? (
                <div style={{ marginTop: '8px' }}>
                  {taxes
                    .filter(tax => tax.title && tax.title.trim() !== '')
                    .map((tax, index) => (
                      <span key={index} style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        marginRight: '8px',
                        marginBottom: '4px',
                        padding: '2px 6px',
                        backgroundColor: '#e9ecef',
                        borderRadius: '3px',
                        fontSize: '0.8em'
                      }}>
                        {tax.title} ({tax.amount}%)
                        <button
                          type="button"
                          onClick={() => {
                            setTaxes(prev => prev.filter((_, i) => i !== index));
                          }}
                          style={{
                            marginLeft: '4px',
                            background: 'none',
                            border: 'none',
                            color: '#666',
                            cursor: 'pointer',
                            fontSize: '12px',
                            padding: '0',
                            lineHeight: '1'
                          }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                </div>
              ) : null}
            </div>

            {/* Row 5: Cancellation Policy - Fees */}
            {/* Cancellation Policy */}
            <div className="form-group">
              <label>Cancellation Policy</label>
              <textarea
                value={cancellationPolicy}
                onChange={(e) => setCancellationPolicy(e.target.value)}
                placeholder="Free cancellation until X days before check-in..."
                rows="3"
                className="form-textarea"
                style={{ resize: 'none' }} // Make non-resizable
              />
            </div>

            {/* Fees */}
            <div className="form-group">
              <label>Fees</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                <input
                  className="form-input"
                  type="text"
                  placeholder="Enter fee name (e.g., Service Fee, Resort Fee)"
                  style={{ flex: 1 }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const nameInput = e.target;
                      const container = nameInput.closest('.form-group');
                      const amountInput = container.querySelector('input[type="number"]');
                      
                      const name = nameInput.value.trim();
                      const amount = parseFloat(amountInput.value);
                      
                      if (name && amount > 0) {
                        const newFee = { title: name, amount: amount };
                        setFees(prev => [...prev.filter(f => f.title && f.title.trim() !== ''), newFee]);
                        nameInput.value = '';
                        amountInput.value = '';
                      }
                    }
                  }}
                />
                <input
                  className="form-input"
                  type="number"
                  placeholder="$"
                  step="0.01"
                  min="0"
                  style={{ flex: '0 0 100px' }}
                />
                <button
                  type="button"
                  onClick={(e) => {
                    const container = e.target.closest('.form-group');
                    const nameInput = container.querySelector('input[type="text"]');
                    const amountInput = container.querySelector('input[type="number"]');
                    
                    const name = nameInput?.value.trim();
                    const amount = parseFloat(amountInput?.value);
                    
                    if (name && amount > 0) {
                      const newFee = { title: name, amount: amount };
                      setFees(prev => [...prev.filter(f => f.title && f.title.trim() !== ''), newFee]);
                      nameInput.value = '';
                      amountInput.value = '';
                    }
                  }}
                  className="add-button"
                  title="Add fee"
                >
                  +
                </button>
              </div>

              {/* Display added fees as tags */}
              {fees.filter(fee => fee.title && fee.title.trim() !== '').length > 0 ? (
                <div style={{ marginTop: '8px' }}>
                  {fees
                    .filter(fee => fee.title && fee.title.trim() !== '')
                    .map((fee, index) => (
                      <span key={index} style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        marginRight: '8px',
                        marginBottom: '4px',
                        padding: '2px 6px',
                        backgroundColor: '#e9ecef',
                        borderRadius: '3px',
                        fontSize: '0.8em'
                      }}>
                        {fee.title} (${fee.amount})
                        <button
                          type="button"
                          onClick={() => {
                            setFees(prev => prev.filter((_, i) => i !== index));
                          }}
                          style={{
                            marginLeft: '4px',
                            background: 'none',
                            border: 'none',
                            color: '#666',
                            cursor: 'pointer',
                            fontSize: '12px',
                            padding: '0',
                            lineHeight: '1'
                          }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                </div>
              ) : null}
            </div>

            {/* Row 6: Hotel Images - Score */}
            {/* Hotel Images */}
            <div className="form-group">
              <div className="hotel-images-header">
                <label style={{ fontWeight: 'bold' }}>Hotel Images</label>
                <button
                  type="button"
                  onClick={() => setShowImageUploadModal(true)}
                  className="add-button"
                  title="Add Images"
                >
                  +
                </button>
              </div>
              <div className="image-upload-section">
                
                {images.length > 0 && (
                  <div className="uploaded-images-preview">
                    <div className="images-grid">
                      {images.map((image) => (
                        <div key={image.id} className="image-preview-item">
                          <img src={image.url} alt={image.name} />
                <button
                  type="button"
                            className="remove-image-btn"
                            onClick={() => removeImage(image.id)}
                            title="Remove image"
                          >
                            ×
                </button>
                        </div>
                      ))}
                    </div>
                    <div className="images-summary">
                      {images.length} image{images.length !== 1 ? 's' : ''} uploaded
                    </div>
                  </div>
                )}
              </div>
              </div>

            {/* Score */}
            <div className="form-group">
              <label>Score</label>
                <input
                  type="text"
                  value={score}
                  onChange={(e) => setScore(e.target.value)}
                placeholder="e.g., 9.2/10, Excellent, Highly Recommended"
                className="form-input"
              />
            </div>

            {/* Row 7: Hotel Features - Select Stay Dates & Set Rates */}
            {/* Hotel Features */}
            <div className="form-group">
              <label>Hotel Features</label>
              
              {/* React-Quill Rich Text Editor */}
              <div className="quill-editor-container">
                <ReactQuill
                  value={featuresHtml}
                  onChange={setFeaturesHtml}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Add hotel features with rich formatting..."
                style={{
                    height: '200px',
                    marginBottom: '50px'
                }}
              />
              </div>
            </div>

            {/* Select Stay Dates & Set Rates */}
            <div className="form-group">
              <label>
                Select Stay Dates & Set Rates *
              </label>
              
              {calendarDates.length > 0 ? (
                <>
                  {/* Scrollable dates container */}
                <div className="inline-dates-table-container">
                  <table className="inline-dates-table">
                    <tbody>
                      {calendarDates.map((date, index) => {
                        const dateStr = date.toISOString().split('T')[0];
                        const isSelected = selectedDates && Array.isArray(selectedDates) && selectedDates.includes(dateStr);
                        const rate = (dateRates && typeof dateRates === 'object') ? (dateRates[dateStr] || '') : '';
                        
                        return (
                          <tr key={index} className={`date-row ${isSelected ? 'selected' : ''}`}>
                            <td className="date-rate-cell">
                              <div className="date-rate-container">
                                <button
                                  onClick={() => handleDateClick(date)}
                                  className={`inline-date-button ${isSelected ? 'selected' : ''}`}
                                  title={`${date.toLocaleDateString('en-US', { 
                                    weekday: 'long', 
                                    year: 'numeric', 
                                    month: 'long', 
                                    day: 'numeric' 
                                  })} - ${isSelected ? 'Selected' : 'Click to select'}`}
                                  style={{
                                      width: '90px',
                                      height: '32px',
                                    padding: '4px 8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    backgroundColor: isSelected ? '#007bff' : '#fff',
                                    color: isSelected ? '#fff' : '#333',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px',
                                    transition: 'all 0.2s ease',
                                    margin: '2px'
                                  }}
                                >
                                  <div className="date-display" style={{ 
                                    display: 'flex', 
                                      flexDirection: 'row',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                      gap: '3px',
                                    fontSize: '12px',
                                    lineHeight: '1.2'
                                  }}>
                                    <span className="day-number" style={{ 
                                      fontSize: '12px', 
                                      fontWeight: 'normal',
                                      minWidth: '16px',
                                      textAlign: 'center'
                                    }}>
                                      {date.getDate().toString().padStart(2, '0')}
                                    </span>
                                    <span className="month-name" style={{ 
                                      fontSize: '11px',
                                      fontWeight: 'normal'
                                    }}>
                                      {date.toLocaleDateString('en-US', { month: 'short' })}
                                    </span>
                                    <span className="weekday-name" style={{ 
                                      fontSize: '10px',
                                      fontWeight: 'normal',
                                      opacity: '0.8'
                                    }}>
                                      {date.toLocaleDateString('en-US', { weekday: 'short' })}
                                    </span>
                                  </div>
                                </button>
                                
                                <div className="rate-input-container" style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}>
                                  {isSelected ? (
                                    <>
                                    <input
                                      type="number"
                                        value={freeDates[dateStr] ? '' : rate}
                                      onChange={(e) => handleDateRateChange(dateStr, e.target.value)}
                                      className="compact-rate-input"
                                      min="0"
                                      step="0.01"
                                      autoFocus
                                          disabled={freeDates[dateStr]}
                                        placeholder={freeDates[dateStr] ? 'Free' : ''}
                                        onKeyDown={(e) => {
                                          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                            e.preventDefault();
                                          }
                                        }}
                                        style={{
                                            width: '275px',
                                          padding: '2px 4px',
                                          border: '1px solid #ccc',
                                          borderRadius: '3px',
                                          fontSize: '12px'
                                        }}
                                      />
                                      <label style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '2px',
                                        fontSize: '10px',
                                        cursor: 'pointer',
                                        userSelect: 'none'
                                      }}>
                                        <input
                                          type="checkbox"
                                          checked={freeDates[dateStr] || false}
                                          onChange={() => handleFreeToggle(dateStr)}
                                          style={{
                                            width: '12px',
                                            height: '12px',
                                            cursor: 'pointer'
                                          }}
                                        />
                                        Free
                                      </label>
                                    </>
                                  ) : (
                                    <span className="rate-placeholder">Rate</span>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      </tbody>
                    </table>
            </div>
                      
                  {/* Total summary - always visible outside scroll */}
                    <div style={{ 
                      marginTop: '10px', 
                      padding: '10px', 
                      backgroundColor: '#f8f9fa', 
                      border: '1px solid #e0e0e0', 
                      borderRadius: '4px' 
                    }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8em', fontWeight: 'normal' }}>
                                <input
                                  type="checkbox"
                                  checked={taxIncluded}
                                  onChange={(e) => setTaxIncluded(e.target.checked)}
                                  style={{ margin: 0 }}
                                />
                                <span>Tax Included</span>
                              </label>
                              <div>
                                <strong>Total ({selectedDates && selectedDates.length > 0 ? selectedDates.length : 0} night{(selectedDates && selectedDates.length !== 1) || !selectedDates || selectedDates.length === 0 ? 's' : ''}): $
                                {(() => {
                                  const subtotal = selectedDates && Array.isArray(selectedDates) ? selectedDates.reduce((total, dateStr) => {
                                    const rate = Math.round((parseFloat(dateRates && typeof dateRates === 'object' ? dateRates[dateStr] : 0) || 0) * 100) / 100;
                                    return total + rate;
                                  }, 0) : 0;
                                  return subtotal.toFixed(2);
                                })()}
                                </strong>
                              </div>
                            </div>
                </div>
                </>
              ) : (
                <p className="no-dates-available">
                  No trip dates available. Please set proposal start and end dates first.
                </p>
              )}
            </div>

            {/* Row 8: Notes - Payment Due */}
            {/* Notes */}
            <div className="form-group">
              <label>Notes</label>
              
              {/* React-Quill Rich Text Editor */}
              <div className="quill-editor-container">
                <ReactQuill
                  value={notesHtml}
                  onChange={setNotesHtml}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Add additional notes with rich formatting..."
                style={{
                    height: '150px',
                    marginBottom: '50px'
                }}
              />
              </div>
            </div>

            {/* Payment Due */}
            <div className="form-group">
              <label>Payment Due</label>
              <select
                value={paymentDue}
                onChange={(e) => setPaymentDue(e.target.value)}
                className="form-input"
              >
                <option value="100% at booking">100% at booking</option>
                <option value="100% due later">100% due later</option>
                <option value="Custom amount at booking">Custom amount at booking</option>
              </select>
              
              {paymentDue === 'Custom amount at booking' && (
                <div style={{ marginTop: '8px' }}>
              <input
                    type="number"
                    value={customPaymentAmount}
                    onChange={(e) => setCustomPaymentAmount(e.target.value)}
                    placeholder="Enter amount due at booking"
                className="form-input"
                    min="0"
                    step="0.01"
                    style={{ width: '100%' }}
                  />
                  <div style={{ fontSize: '0.8em', color: '#666', marginTop: '4px' }}>
                    {customPaymentAmount && selectedDates.length > 0 && (() => {
                      const totalCost = selectedDates.reduce((total, dateStr) => {
                        const rate = parseFloat(dateRates[dateStr] || 0);
                        return total + rate;
                      }, 0);
                      const customAmount = parseFloat(customPaymentAmount) || 0;
                      const remainder = Math.max(0, totalCost - customAmount);
                      return `Remainder due later: $${remainder.toFixed(2)}`;
                    })()}
                  </div>
                </div>
              )}
            </div>

            


            {/* Discounts - HIDDEN */}
            {/* <div className="form-group">
                <label>Discounts</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                  <input
                    className="form-input"
                    type="text"
                    placeholder="Enter discount name (e.g., Early Bird, Group Discount)"
                    style={{ flex: 1 }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const nameInput = e.target;
                        const container = nameInput.closest('.form-group');
                        const typeSelect = container.querySelector('select');
                        const amountInput = container.querySelector('input[type="number"]');
                        
                        const name = nameInput.value.trim();
                        const type = typeSelect.value;
                        const amount = parseFloat(amountInput.value);
                        
                        if (name && amount > 0) {
                          const newDiscount = { title: name, type: type, amount: amount };
                          setDiscounts(prev => [...prev.filter(d => d.title && d.title.trim() !== ''), newDiscount]);
                          nameInput.value = '';
                          typeSelect.value = 'percentage';
                          amountInput.value = '';
                        }
                      }
                    }}
                  />
                  <select 
                    className="form-input"
                    defaultValue="percentage"
                    style={{ flex: '0 0 60px' }}
                  >
                    <option value="percentage">%</option>
                    <option value="fixed">$</option>
                  </select>
                  <input
                    className="form-input"
                    type="number"
                    placeholder="Amount"
                    step="0.01"
                    min="0"
                    style={{ flex: '0 0 100px' }}
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      const container = e.target.closest('.form-group');
                      const nameInput = container.querySelector('input[type="text"]');
                      const typeSelect = container.querySelector('select');
                      const amountInput = container.querySelector('input[type="number"]');
                      
                      const name = nameInput?.value.trim();
                      const type = typeSelect?.value;
                      const amount = parseFloat(amountInput?.value);
                      
                      if (name && amount > 0) {
                        const newDiscount = { title: name, type: type, amount: amount };
                        setDiscounts(prev => [...prev.filter(d => d.title && d.title.trim() !== ''), newDiscount]);
                        nameInput.value = '';
                        typeSelect.value = 'percentage';
                        amountInput.value = '';
                      }
                    }}
                    className="add-button"
                    title="Add discount"
                  >
                    +
                  </button>
                </div>

                  <div style={{ marginTop: '8px' }}>
                    {discounts
                      .filter(discount => discount.title && discount.title.trim() !== '')
                      .map((discount, index) => (
                        <span key={index} style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          marginRight: '8px',
                          marginBottom: '4px',
                          padding: '2px 6px',
                          backgroundColor: '#e9ecef',
                          borderRadius: '3px',
                          fontSize: '0.8em'
                        }}>
                          {discount.title} ({discount.type === 'percentage' ? `${discount.amount}%` : `$${discount.amount}`})
                          <button
                            type="button"
                            onClick={() => {
                              setDiscounts(prev => prev.filter((_, i) => i !== index));
                            }}
                            style={{
                              marginLeft: '4px',
                              background: 'none',
                              border: 'none',
                              color: '#666',
                              cursor: 'pointer',
                              fontSize: '12px',
                              padding: '0',
                              lineHeight: '1'
                            }}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                  </div>
                )}
              </div> */}

          </div>
          
          <div className="modal-actions">
            <button className="modal-action-button" onClick={onClose}>Cancel</button>
            {editingHotel && (
              <button className="modal-action-button" onClick={() => handleSubmit(false)}>
                Update
              </button>
            )}
            <button className="modal-action-button" onClick={() => handleSubmit(true)}>
              {editingHotel ? 'Add New' : 'Add'}
            </button>
          </div>
        </div>
      </div>

      {/* Image Upload Modal */}
      <ImageUploadModal
        isOpen={showImageUploadModal}
        onClose={() => setShowImageUploadModal(false)}
        onImagesUploaded={handleImagesUploaded}
        existingImages={images}
        proposalId={proposalId}
        hotelId={editingHotel?._id}
      />
    </div>
  );
};

export default AddProposalHotelModal;